import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import IssueComments from '../components/IssueComments';

interface PortalIssue {
  id: number;
  type: 'bug' | 'feature';
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  created_by_email: string | null;
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'wontfix', label: "Won't fix" },
];

const PortalIssues: React.FC = () => {
  const { user } = useAuth();
  const [issues, setIssues] = useState<PortalIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'bug' | 'feature'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const selected = issues.find((i) => i.id === selectedId);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const res = await api.get<PortalIssue[]>(`/portal-issues?${params.toString()}`);
      setIssues(res.data);
      if (selectedId && !res.data.find((i) => i.id === selectedId)) {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Failed to fetch portal issues:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [filterType, filterStatus]);

  const updateStatus = async (id: number, status: string) => {
    setUpdatingStatus(id);
    try {
      await api.patch(`/portal-issues/${id}`, { status });
      setIssues((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selectedId === id) {
        setSelectedId(null);
        setSelectedId(id);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (s: string) => {
    const d = new Date(s);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86400000) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return d.toLocaleDateString(undefined, { weekday: 'short' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bugs & feature requests</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Portal feedback tracker. Only visible to global admins and global users.
          </p>
        </div>
        <Link
          to="/feedback"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover"
        >
          + New
        </Link>
      </div>

      <div className="flex flex-wrap gap-4 items-center">
        <span className="text-sm text-gray-600 dark:text-gray-400">Filters:</span>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as 'all' | 'bug' | 'feature')}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-brand focus:border-brand"
        >
          <option value="all">All types</option>
          <option value="bug">Bugs</option>
          <option value="feature">Features</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-brand focus:border-brand"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>
        ) : issues.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No issues match the filters.</div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {issues.map((issue) => (
              <li key={issue.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div
                  className="px-4 py-3 flex items-start gap-3 cursor-pointer"
                  onClick={() => setSelectedId(selectedId === issue.id ? null : issue.id)}
                >
                  <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-mono text-sm">#{issue.id}</span>
                  <span
                    className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      issue.type === 'bug'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {issue.type === 'bug' ? 'Bug' : 'Feature'}
                  </span>
                  <span
                    className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      issue.status === 'open'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                        : issue.status === 'in_progress'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                        : issue.status === 'done'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {STATUS_OPTIONS.find((o) => o.value === issue.status)?.label || issue.status}
                  </span>
                  <span className="flex-1 min-w-0 font-medium text-gray-900 dark:text-gray-100 truncate">
                    {issue.title}
                  </span>
                  <span className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
                    {issue.created_by_name || issue.created_by_email || 'Unknown'}
                  </span>
                  <span className="flex-shrink-0 text-sm text-gray-400 dark:text-gray-500" title={issue.created_at}>
                    {formatDate(issue.created_at)}
                  </span>
                </div>
                {selectedId === issue.id && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 w-full">
                    <div className="pt-3 space-y-3 w-full min-w-0">
                      {issue.description ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownRenderer content={issue.description || ''} />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No description.</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                        <select
                          value={issue.status}
                          onChange={(e) => updateStatus(issue.id, e.target.value)}
                          disabled={updatingStatus === issue.id}
                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Updated {new Date(issue.updated_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <IssueComments issueId={issue.id} />
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PortalIssues;
